// Client-side replica of the server annual (SchemeOutput.AmountPayable) + monthly
// (PaymentPlanProcessor.ProcessPaymentPlan), so a new-business select toggle updates the monthly instantly.

// C# Math.Round default is MidpointRounding.ToEven (banker's); premium Net/Ipt are stored at 4dp.
function round4(x: number): number {
    const n = x * 1e4;
    const floor = Math.floor(n);
    const diff = n - floor;
    let r: number;
    if (Math.abs(diff - 0.5) < 1e-6) r = (floor % 2 === 0) ? floor : floor + 1; // exact half → nearest even
    else r = Math.round(n);
    return r / 1e4;
}
// Money.RoundToPenny = Math.Round(x, 2, AwayFromZero). Optimistic path is new-business only (premiums ≥ 0).
function penny(x: number): number {
    return (x < 0 ? -1 : 1) * Math.round(Math.abs(x) * 100 + 1e-6) / 100;
}

type PlanParams = { n: number; fee: number; rate: number; requiresDeposit: boolean; depositPct: number | null };
type Monthly = { installment: number; firstInstallment: number; deposit: number; totalPayable: number };

// A selected scheme on the product output.
const selectedScheme = (productOutput: any): any =>
    (productOutput?.ReturnSchemes ?? []).find((s: any) => s.IsSelected) ?? productOutput?.ReturnSchemes?.[0] ?? null;

// SchemeOutput.AmountPayable: penny(round4(Σ selected cover Net + Σ Ipt)) + Σ selected, non-included addons.
function computeAnnual(scheme: any): number {
    const covers = (scheme?.Covers ?? []).filter((c: any) => c.IsMandatory || c.IsSelected);
    let net = 0;
    let ipt = 0;
    for (const c of covers) {
        net += Number(c?.QuotedPremium?.Net) || 0;
        ipt += Number(c?.QuotedPremium?.Ipt) || 0;
    }
    let total = penny(round4(net + ipt));
    const addons = [
        ...(scheme?.Addons ?? []),
        ...((scheme?.GroupedAddons ?? []).flatMap((g: any) => g?.GroupAddons ?? [])),
    ];
    for (const a of addons) {
        if (a?.IsSelected && !a?.IsIncluded) total += penny(Number(a?.TotalPayable) || 0);
    }
    return total;
}

const planParams = (plan: any): PlanParams => ({
    n: Number(plan?.NumberOfInstallments) || 0,
    fee: Number(plan?.Fee) || 0,
    rate: (Number(plan?.APR) || 0) / 2, // CalculatedPaymentPlan.APR = PaymentPlan.Interest * 2
    requiresDeposit: plan?.RequiresDeposit === true,
    depositPct: plan?.DepositPercentage == null ? null : Number(plan.DepositPercentage),
});

// Faithful replica of ProcessPaymentPlan's headline figures for the given premium and plan params.
function processPlan(premiumIn: number, p: PlanParams, financeFee: boolean): Monthly {
    const premium = penny(premiumIn);
    let loan = premium + p.fee;
    const settled = financeFee ? premium + p.fee : premium;
    let deposit = 0;
    if (p.requiresDeposit && p.depositPct != null) {
        deposit = penny(premium * (1 + p.depositPct / 100) - premium);
        loan = settled - deposit;
    }
    let installment = 0;
    let totalPayable = 0;
    if (p.rate !== 0) {
        const interest = penny(loan * (1 + p.rate / 100) - loan);
        installment = penny((loan + interest) / p.n);
        totalPayable = deposit + loan + interest;
    } else {
        installment = penny(loan / p.n);
        const instalmentTotal = installment * p.n;
        deposit = settled - instalmentTotal;
        totalPayable = instalmentTotal + deposit;
    }
    let firstInstallment = installment;
    if (!p.requiresDeposit && deposit !== 0) { firstInstallment += deposit; deposit = 0; }
    return { installment, firstInstallment, deposit, totalPayable };
}

// TempPaymentPlanFinanceFee isn't visible to the client — pick whichever setting reproduces the last plan.
function inferFinanceFee(p: PlanParams, lastPlan: any): boolean {
    const old = Number(lastPlan?.BaseTransactionAmount) || 0;
    const target = Number(lastPlan?.InstallmentAmount);
    if (!old || !Number.isFinite(target)) return false;
    const on = processPlan(old, p, true);
    const off = processPlan(old, p, false);
    const onInst = Math.abs(on.installment - target) < 0.005;
    const offInst = Math.abs(off.installment - target) < 0.005;
    if (onInst && !offInst) return true;
    if (offInst && !onInst) return false;
    // Installment alone doesn't disambiguate (e.g. no deposit) — the fee only shifts the folded remainder.
    const targetFirst = Number(lastPlan?.FirstInstallmentAmount ?? lastPlan?.InstallmentAmount);
    return Math.abs(on.firstInstallment - targetFirst) < Math.abs(off.firstInstallment - targetFirst);
}

export interface OptimisticPrediction { annual: number; installment: number | null }

export function useOptimisticPremium() {
    // Write optimistic annual + monthly onto the selected scheme; returns the figures for a drift check.
    function applyOptimistic(productOutput: any): OptimisticPrediction | null {
        const scheme = selectedScheme(productOutput);
        if (!scheme) return null;
        const annual = computeAnnual(scheme);
        const lastPlan = scheme.PaymentPlan;
        const p = lastPlan ? planParams(lastPlan) : null;
        // Annual-only quote (no financed plan) — the annual is already reactive, nothing to write.
        if (!lastPlan || lastPlan.InstallmentAmount == null || !p || !p.n) {
            scheme.QuotedPremium = { ...(scheme.QuotedPremium ?? {}), TotalPayable: annual };
            return { annual, installment: null };
        }
        const m = processPlan(annual, p, inferFinanceFee(p, lastPlan));
        // Keep the scheme total and plan in step so monthlyFactor (InstallmentAmount / total) stays correct.
        scheme.QuotedPremium = { ...(scheme.QuotedPremium ?? {}), TotalPayable: annual };
        scheme.PaymentPlan = {
            ...lastPlan,
            InstallmentAmount: m.installment,
            FirstInstallmentAmount: m.firstInstallment,
            DepositAmount: m.deposit,
            TotalPayable: m.totalPayable,
        };
        return { annual, installment: m.installment };
    }

    // Compare the optimistic figures against the authoritative server response; warn on drift > 1p.
    function reportDrift(predicted: OptimisticPrediction | null, serverOutput: any): void {
        if (!predicted || predicted.installment == null) return;
        const scheme = selectedScheme(serverOutput);
        const server = Number(scheme?.PaymentPlan?.InstallmentAmount);
        if (scheme?.PaymentPlan?.InstallmentAmount == null || !Number.isFinite(server)) return;
        const drift = Math.abs(server - predicted.installment);
        if (drift > 0.01) {
            // eslint-disable-next-line no-console
            console.warn('[optimistic-rerate] monthly drift', {
                predicted: predicted.installment, server, drift: Number(drift.toFixed(4)),
            });
        }
    }

    return { applyOptimistic, reportDrift };
}
